import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { api_service } from '../../api/api-service';
import { api_urls } from '../../api/api-urls';
import { PasswordSetup } from '../../components';
import ButtonComponent from '../../components/button/ButtonComponent';
import { CommonLoginWrapper } from '../../components/global-styles/GlobalStyles';
import { BrandLogo } from '../../components/ui-components/BrandLogo';
import { hideLoader } from '../../store/reducers/appSlice';
import { clearImpersonation } from '../../store/reducers/impersonationSlice';
import { openToast } from '../../store/reducers/toastSlice';
import { updateLoginStatus } from '../../store/reducers/userSlice';
import { validateJwtToken } from '../../utils/jwt-utils';
import { setCurrentDomainMigrationCookie } from '../../utils/migration-cookies';
import { BrandLogoStyle, DescriptionText, LoginCardStack, LogoContainer } from '../registration/registration-styles';

interface SelfRegistrationState {
    isValidating: boolean;
    isTokenValid: boolean;
    isPasswordSetComplete: boolean;
    tokenError: string | null;
    token: string | null;
    userData: { id?: number | string; email?: string; registrationId?: number | string; portalId?: number } | null;
}

/**
 * Self-registration page for users created from the admin portal.
 * Users access via: /self-registration?token=<jwt-token>
 * Flow: Token validation → Password setup → Login redirect
 */
export const SelfRegistration: React.FC = () => {
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();
    const [state, setState] = useState<SelfRegistrationState>({
        isValidating: true,
        isTokenValid: false,
        isPasswordSetComplete: false,
        tokenError: null,
        token: null,
        userData: null,
    });

    useEffect(() => {
        const token = searchParams.get('token');

        // Validate token presence and format
        const validation = validateJwtToken(token || '');

        if (!validation.isValid) {
            setState({
                isValidating: false,
                isTokenValid: false,
                isPasswordSetComplete: false,
                tokenError:
                    validation.error || 'Invalid or missing registration token. Please check your link and try again.',
                token: null,
                userData: null,
            });
            return;
        }

        // Token format is valid, now validate with backend
        const validateWithBackend = async () => {
            try {
                const response = (await api_service.get({
                    endPoint: `${api_urls.ValidateActivationLink}?token=${encodeURIComponent(token || '')}`,
                    showLoader: false,
                })) as {
                    data?: {
                        statusCode?: number;
                        message?: string;
                        data?: { valid?: boolean; id?: string | number; email?: string; portalId?: number };
                    };
                };

                if (response?.data?.statusCode === 200 && response?.data?.data?.valid) {
                    // Backend validation successful - use response data which includes portalId
                    const backendData = response?.data?.data;
                    setState({
                        isValidating: false,
                        isTokenValid: true,
                        isPasswordSetComplete: false,
                        tokenError: null,
                        token: token?.trim() || null,
                        userData: {
                            id: backendData?.id,
                            email: backendData?.email,
                            portalId: backendData?.portalId,
                        },
                    });
                } else {
                    // Backend validation failed
                    setState({
                        isValidating: false,
                        isTokenValid: false,
                        isPasswordSetComplete: false,
                        tokenError:
                            response?.data?.message ||
                            'This activation link is invalid or has expired. Please request a new one.',
                        token: null,
                        userData: null,
                    });
                }
            } catch {
                setState({
                    isValidating: false,
                    isTokenValid: false,
                    isPasswordSetComplete: false,
                    tokenError: 'Failed to validate activation link. Please try again.',
                    token: null,
                    userData: null,
                });
            }
        };

        validateWithBackend();
    }, [searchParams]);

    const handlePasswordSetupComplete = async (password: string) => {
        if (!state.token) {
            dispatch(
                openToast({
                    show: true,
                    isAlertOpen: true,
                    message: 'Token not found. Please try again.',
                    severity: 'error',
                }),
            );
            return;
        }

        try {
            const email = state.userData?.email;
            const portalId = state.userData?.portalId;

            if (!email || !portalId) {
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: 'Email or Portal ID not found. Please try again.',
                        severity: 'error',
                    }),
                );
                return;
            }

            // Call forgot-password/reset endpoint for self-registration
            const payload = {
                email,
                password,
                portalId,
            };

            const response = (await api_service.post({
                endPoint: api_urls.ResetForgotPassword,
                payLoad: payload,
                showLoader: true,
            })) as { data?: { statusCode?: number; message?: string } };

            const statusCode = response?.data?.statusCode;
            if (statusCode === 200 || statusCode === 201) {
                // Password set successfully
                setState(prev => ({
                    ...prev,
                    isPasswordSetComplete: true,
                }));
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: 'Password set successfully.',
                        severity: 'success',
                    }),
                );
            } else {
                const errorMessage =
                    response?.data?.message || 'Failed to set password. Please check your details and try again.';
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: errorMessage,
                        severity: 'error',
                    }),
                );
            }
        } catch (err: unknown) {
            const apiError = err as { message?: string; response?: { data?: { message?: string } } };
            const errorMessage =
                apiError?.message ||
                apiError?.response?.data?.message ||
                'An error occurred while setting your password.';
            dispatch(
                openToast({
                    show: true,
                    isAlertOpen: true,
                    message: errorMessage,
                    severity: 'error',
                }),
            );
        } finally {
            dispatch(hideLoader());
        }
    };

    if (state.isValidating) {
        return (
            <CommonLoginWrapper>
                <LoginCardStack className="loginCard" $currentStep="validating">
                    <Stack alignItems="center" spacing={2}>
                        <CircularProgress />
                        <Typography variant="body1">Loading...</Typography>
                    </Stack>
                </LoginCardStack>
            </CommonLoginWrapper>
        );
    }

    if (!state.isTokenValid) {
        return (
            <CommonLoginWrapper>
                <LoginCardStack className="loginCard" $currentStep="error">
                    <LogoContainer>
                        <BrandLogo alt="Brand Logo" style={BrandLogoStyle} />
                    </LogoContainer>
                    <DescriptionText>
                        <Typography variant="h5" component="h1" mb={2}>
                            Registration Error
                        </Typography>
                        <Typography variant="body1" color="error">
                            {state.tokenError}
                        </Typography>
                        <Box mt={3}>
                            <Typography variant="body2" color="textSecondary">
                                If you believe this is a mistake, please contact support.
                            </Typography>
                        </Box>
                    </DescriptionText>
                </LoginCardStack>
            </CommonLoginWrapper>
        );
    }

    const handleGoToLogin = async () => {
        try {
            // Try to call logout API
            const authToken = localStorage.getItem('authToken');
            if (authToken) {
                await api_service.post({
                    endPoint: api_urls.Logout,
                    payLoad: {},
                    showLoader: false,
                });
            }
        } catch (error) {
            console.error('Logout error:', error);
        }

        // Aggressively clear ALL session/storage data
        localStorage.clear();
        sessionStorage.clear();

        // Set migration cookie to allow login access through NoCookieGuard
        setCurrentDomainMigrationCookie({ isMigrated: true, Registered: true });

        // Clear Redux state - both login and impersonation to prevent stale modal
        dispatch(updateLoginStatus(false));
        dispatch(clearImpersonation());

        // Navigate directly to login - no parameters, completely fresh session
        setTimeout(() => {
            window.location.replace('/login');
        }, 100);
    };

    if (state.isPasswordSetComplete) {
        return (
            <CommonLoginWrapper>
                <LoginCardStack className="loginCard" $currentStep="success">
                    <LogoContainer>
                        <BrandLogo alt="Brand Logo" style={BrandLogoStyle} />
                    </LogoContainer>
                    <DescriptionText>
                        <Typography variant="h5" component="h1" mb={2}>
                            Password Set Successfully
                        </Typography>
                        <Typography variant="body1" color="textSecondary" mb={3}>
                            Your password has been set. You can now use this password to log in to your account.
                        </Typography>
                        <Box mt={3} display="flex" flexDirection="column" alignItems="center">
                            <ButtonComponent variant="contained" onClick={handleGoToLogin} sx={{ mb: 2, width: 200 }}>
                                Go to Login
                            </ButtonComponent>
                            <Typography variant="body2" color="textSecondary" mt={2}>
                                Or you can close this window.
                            </Typography>
                        </Box>
                    </DescriptionText>
                </LoginCardStack>
            </CommonLoginWrapper>
        );
    }

    return (
        <CommonLoginWrapper>
            <LoginCardStack className="loginCard" $currentStep="password">
                <LogoContainer>
                    <BrandLogo alt="Brand Logo" style={BrandLogoStyle} />
                </LogoContainer>

                {/* Password Setup Form */}
                <Box sx={{ width: '100%', px: 4, pb: 4 }}>
                    <PasswordSetup onPasswordSetupComplete={handlePasswordSetupComplete} forgotPassword={true} />
                </Box>
            </LoginCardStack>
        </CommonLoginWrapper>
    );
};
